/* eslint-disable max-lines-per-function */
import {
  data,
  data2,
} from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/data';
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/Arbeitsbereich';
import { KalenderWoche } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/KalenderWoche';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/Mitarbeiter';
import { RowDivider } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/RowDivider';
import { ArbeitsbereichKalenderStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/ArbeitsbereichKalenderStation';
import { MitarbeiterKalenderStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/MitarbeiterKalenderStation';
import {
  KalenderProvider,
  useKalenderContext,
} from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/KalenderContext';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { CSSProperties, useRef } from 'react';

type StyleProps = CSSProperties & {
  '--col-start': CSSProperties['gridColumnStart'];
};

type StylePropsGrid = CSSProperties & {
  '--col-count': CSSProperties['gridTemplateColumns'];
};

export function KalenderContainer({
  jahr,
  view,
}: {
  jahr: string;
  view: 'mitarbeiter' | 'arbeitsbereiche';
}) {
  return (
    <KalenderProvider>
      <div className="w-full relative">
        <Kalender jahr={jahr} view={view} />
      </div>
    </KalenderProvider>
  );
}

export function Kalender({
  jahr,
  view,
}: {
  jahr: string;
  view: 'mitarbeiter' | 'arbeitsbereiche';
}) {
  const { setIsScrolling } = useKalenderContext();
  const weeksInYear = dayjs(`${jahr}-01-01`).isoWeeksInYear();
  const gridRef = useRef<HTMLDivElement | null>(undefined);

  return (
    <>
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-linear-to-l from-white to-transparent z-10" />
      <div
        className="overflow-x-auto"
        onScroll={() => setIsScrolling(true)}
        // eslint-disable-next-line react/no-unknown-property
        onScrollEnd={() => setIsScrolling(false)}>
        <div
          ref={gridRef}
          className={`grid grid-cols-[1fr_repeat(var(--col-count),8px)] md:grid-cols-[180px_repeat(var(--col-count),16px)] w-fit h-full`}
          style={
            {
              '--col-count': weeksInYear * 7,
            } as StylePropsGrid
          }>
          <div className="sticky left-0 bg-white z-10 row-start-1 border-r border-solid border-neutrals-200"></div>
          {Array.from({ length: weeksInYear }, (_, i) => {
            const startOfWeek = dayjs()
              .year(parseInt(jahr))
              .isoWeek(i + 1)
              .startOf('isoWeek');

            const endOfWeek = dayjs()
              .year(parseInt(jahr))
              .isoWeek(i + 1)
              .endOf('isoWeek');

            const isCurrentWeek =
              dayjs().get('year').toString() === jahr &&
              dayjs().isoWeek() === i + 1;

            return (
              <div
                key={i}
                className={`col-start-(--col-start) col-span-7 row-start-1 p-2`}
                style={
                  {
                    '--col-start': i * 7 + 2,
                  } as StyleProps
                }>
                <KalenderWoche
                  isCurrentWeek={isCurrentWeek}
                  startOfWeek={startOfWeek.format('DD.MM')}
                  endOfWeek={endOfWeek.format('DD.MM')}
                  index={i}
                  gridRef={gridRef}
                />
              </div>
            );
          })}
          <RowDivider />
          {view === 'mitarbeiter'
            ? data.map((mitarbeiter) => {
                return (
                  <>
                    <Mitarbeiter mitarbeiter={mitarbeiter} />
                    {mitarbeiter.stationen
                      .filter(
                        (station) =>
                          dayjs(station.von).get('year').toString() === jahr &&
                          dayjs(station.bis).get('year').toString() === jahr,
                      )
                      .map((station) => (
                        <MitarbeiterKalenderStation
                          key={station.id}
                          station={station}
                        />
                      ))}
                    <RowDivider />
                  </>
                );
              })
            : data2.map((arbeitsbereich) => {
                return (
                  <>
                    <Arbeitsbereich arbeitsbereich={arbeitsbereich} />
                    {arbeitsbereich.stationen
                      .filter(
                        (station) =>
                          dayjs(station.von).get('year').toString() === jahr &&
                          dayjs(station.bis).get('year').toString() === jahr,
                      )
                      .map((station) => (
                        <ArbeitsbereichKalenderStation
                          key={station.id}
                          station={station}
                          arbeitsbereich={arbeitsbereich}
                        />
                      ))}
                    <RowDivider />
                  </>
                );
              })}
        </div>
      </div>
    </>
  );
}
